import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { EmailOtpType } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui';
import { useAuth } from '../contexts/AuthContext';

type ConfirmStatus = 'pending' | 'success' | 'error';
type ConfirmErrorKind =
  | 'invalid_link'
  | 'verification_failed'
  | 'session_failed';

const SUPPORTED_OTP_TYPES: readonly EmailOtpType[] = [
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email',
];

function toEmailOtpType(raw: string | null): EmailOtpType | null {
  if (!raw) return null;
  return (SUPPORTED_OTP_TYPES as readonly string[]).includes(raw)
    ? (raw as EmailOtpType)
    : null;
}

export function AuthConfirmPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { resendVerificationEmail } = useAuth();
  const processed = useRef(false);
  const [status, setStatus] = useState<ConfirmStatus>('pending');
  const [errorKind, setErrorKind] = useState<ConfirmErrorKind | null>(null);
  const [resendState, setResendState] =
    useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const otpTypeParam = toEmailOtpType(searchParams.get('type'));
  const nextParam = searchParams.get('next') || searchParams.get('returnTo');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    async function run() {
      try {
        const tokenHash = searchParams.get('token_hash');
        const code = searchParams.get('code');
        const errorCode =
          searchParams.get('error') ||
          new URLSearchParams(window.location.hash.substring(1)).get('error');
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        );
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (errorCode) {
          setErrorKind('verification_failed');
          setStatus('error');
          return;
        }

        // 1. Preferred path: token_hash + type (cross-context, works across devices)
        if (tokenHash) {
          if (!otpTypeParam) {
            setErrorKind('invalid_link');
            setStatus('error');
            return;
          }
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: otpTypeParam,
          });
          if (error) {
            setErrorKind('verification_failed');
            setStatus('error');
            return;
          }
          finishSuccess(otpTypeParam);
          return;
        }

        // 2. PKCE code (e.g. legacy emails still using {{ .ConfirmationURL }})
        if (code) {
          // detectSessionInUrl may have already exchanged the code
          const existing = await supabase.auth.getSession();
          if (existing.data.session) {
            finishSuccess(otpTypeParam);
            return;
          }
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            setErrorKind('verification_failed');
            setStatus('error');
            return;
          }
          finishSuccess(otpTypeParam);
          return;
        }

        // 3. Implicit-flow hash fallback (#access_token=...&refresh_token=...)
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            setErrorKind('session_failed');
            setStatus('error');
            return;
          }
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search
          );
          finishSuccess(otpTypeParam);
          return;
        }

        setErrorKind('invalid_link');
        setStatus('error');
      } catch {
        setErrorKind('verification_failed');
        setStatus('error');
      }
    }

    function finishSuccess(type: EmailOtpType | null) {
      if (type === 'recovery') {
        navigate('/reset-password/confirm', { replace: true });
        return;
      }
      setStatus('success');
      const target = nextParam || '/';
      window.setTimeout(() => {
        navigate(target, { replace: true });
      }, 1500);
    }

    run();
  }, [navigate, searchParams, otpTypeParam, nextParam]);

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('confirm.processing')}</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    const messageKey =
      errorKind === 'invalid_link'
        ? 'confirm.errorInvalidLink'
        : errorKind === 'session_failed'
          ? 'confirm.errorSessionFailed'
          : 'confirm.errorVerificationFailed';

    const handleResend = async () => {
      const email = window.prompt(t('login.resendVerificationEmail'));
      if (!email) return;
      setResendState('sending');
      const { error } = await resendVerificationEmail(email);
      setResendState(error ? 'failed' : 'sent');
    };

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('confirm.errorTitle')}
          </h1>
          <p className="text-gray-600 mb-6">{t(messageKey)}</p>
          {otpTypeParam === 'signup' && resendState !== 'sent' && (
            <Button
              onClick={handleResend}
              disabled={resendState === 'sending'}
              className="w-full mb-3"
            >
              {t('confirm.resendCta')}
            </Button>
          )}
          {resendState === 'sent' && (
            <p className="text-sm text-green-700 mb-3">
              {t('login.resendSuccessMessage')}
            </p>
          )}
          <Button
            onClick={() => navigate('/login')}
            variant="secondary"
            className="w-full mb-3"
          >
            {t('confirm.loginCta')}
          </Button>
          <p className="text-xs text-gray-500">{t('confirm.supportHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('confirm.successTitle')}
        </h1>
        <p className="text-gray-600 mb-4">{t('confirm.successMessage')}</p>
        <p className="text-sm text-gray-500">{t('confirm.redirecting')}</p>
      </div>
    </div>
  );
}
